import { useEffect, useState, useRef } from "react";
import { useModal } from "../context/ModalProvider";
import { useQuery } from "@tanstack/react-query";
import ModalAddNotes from "../jsx/module/ActiveVisit/Assesment/ModalAddNotes";
import { getAnnotationPhoto } from "../services/AssesmentService";
import { resolveStorageUrl } from "../utils/resolveStorageUrl";

export default function useCanvasAnatomi(patientGender = null) {
  const initialGender = patientGender || "MALE";
  const [clickLayer, setClickLayer] = useState(false);
  const [selectedImage, setSelectedImage] = useState(`${initialGender}_FRONT`);
  const [activeType, setActiveType] = useState(initialGender);

  // Sync activeType when patientGender resolves after mount (async patient fetch).
  useEffect(() => {
    if (patientGender) setActiveType(patientGender);
  }, [patientGender]);
  const [positionAnnotation, setPossitionAnnotation] = useState([]);
  const [dataAnnotation, setDataAnnotation] = useState([]);
  const [summaryAnnotation, setSummaryAnnotation] = useState("");

  const { isLoading: isLoadingAnnotation } = useQuery(
    ["AnnotationPhoto"],
    getAnnotationPhoto,
    {
      onSuccess: (res) => {
        const normalized = (res.data || []).map((row) => ({
          ...row,
          imageUrl: resolveStorageUrl(row.imageUrl),
        }));
        setDataAnnotation(normalized);
      },
    }
  );

  const modal = useModal();
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  // Monotonic token to discard stale image.onload callbacks when the draw
  // effect re-runs before a previous image finishes loading (gender flip
  // race: MALE preload can resolve after FEMALE and overwrite the canvas).
  const drawTokenRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    contextRef.current = context;

    // Don't kick off a draw (and its MALE-default preload) until the async
    // patient gender has resolved. Prevents the initial MALE→FEMALE race.
    if (!patientGender) return;

    const token = ++drawTokenRef.current;

    if (dataAnnotation.length > 0) {
      const imageFilter = dataAnnotation.find(
        (value) => value.subType === selectedImage
      );

      const image = new Image();
      image.src = imageFilter?.imageUrl;

      image.onload = () => {
        // Bail if a newer draw has superseded this one.
        if (token !== drawTokenRef.current) return;
        canvas.height = 510;
        canvas.width = 335;

        context.drawImage(image, 0, 0, canvas.width, canvas.height);
      };
    }

    // eslint-disable-next-line
  }, [selectedImage, dataAnnotation, patientGender]);

  useEffect(() => {
    const imageFilter = dataAnnotation.find(
      (value) => value.subType === `${activeType}_FRONT`
    );

    setSelectedImage(imageFilter?.subType);
  }, [activeType, dataAnnotation]);

  useEffect(() => {
    const summary = positionAnnotation.flatMap((annotation) =>
      annotation.annotationDetails.map((detail) => detail.description)
    );

    setSummaryAnnotation(summary.join(", "));
  }, [positionAnnotation]);

  const handleSetPosition = (e) => {
    const dataPosition = [...positionAnnotation];
    const index = e.index;

    const imageFilter = dataAnnotation.find(
      (value) => value.subType === selectedImage
    );
    const filterSelectedImg = dataPosition.filter(
      (value) => value.type === selectedImage
    );

    if (filterSelectedImg.length <= 0) {
      dataPosition.push({
        type: imageFilter.subType,
        photoUrl: imageFilter.imageUrl,
        annotationDetails: [
          {
            x: e.layerX,
            y: e.layerY,
            dx: e.dx || 80,
            dy: e.dy || 80,
          },
        ],
      });
    }

    const selectedPositionImg = filterSelectedImg[0];

    if (
      selectedPositionImg.annotationDetails.filter(
        (_, indexP) => indexP === index
      ).length > 0
    ) {
      const selectedPosition = selectedPositionImg.annotationDetails[index];
      selectedPosition.x = e.layerX;
      selectedPosition.y = e.layerY;
      selectedPosition.dy = e.dy || 80;
      selectedPosition.dx = e.dx || 80;
    } else {
      selectedPositionImg.annotationDetails.push({
        x: e.layerX,
        y: e.layerY,
        dx: e.dx || 80,
        dy: e.dy || 80,
      });
    }

    setPossitionAnnotation(dataPosition);
  };

  const handleAddPosition = (e) => {
    modal.show(
      () => (
        <ModalAddNotes
          handleDismisModal={() => modal.dismiss()}
          positionAnnotation={positionAnnotation}
          dataAnnotation={dataAnnotation}
          selectedImage={selectedImage}
          setPossitionAnnotation={(value) => setPossitionAnnotation(value)}
          event={e}
        />
      ),
      { centered: true }
    );
  };

  const handleChangeType = (value) => {
    setActiveType(value);
  };

  const handleUndo = () => {
    const dataPosition = [...positionAnnotation];
    const filterSelectedImg = dataPosition.find(
      (value) => value.type === selectedImage
    );

    if (filterSelectedImg?.annotationDetails.length > 0) {
      filterSelectedImg?.annotationDetails.pop();
    }

    setPossitionAnnotation(dataPosition);
  };

  const activeAnnotation =
    positionAnnotation.find((value) => value.type === selectedImage) || null;

  return {
    canvasRef,
    isLoadingAnnotation,
    activeAnnotation,
    positionAnnotation,
    dataAnatomis: dataAnnotation || [],
    handleSetPosition,
    setSelectedImage,
    handleAddPosition,
    setClickLayer,
    clickLayer,
    activeType,
    handleChangeType,
    selectedImage,
    handleUndo,
    setPossitionAnnotation,
    summaryAnnotation,
  };
}
